/**
 * The search form (Lane planner left pane): one origin and one destination box
 * for every mode, shared dates, then a cargo section for each ticked mode, all
 * in one form so values survive ticking a mode off and on. It emits one
 * vendor-neutral request covering the ticked modes (laneForm.ts); each vendor
 * adapter on the backend translates it.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'antd';

import { CustomDatePicker, CustomSelect } from '../../../components';
import type { LaneMode, LaneSearchRequest } from '../../../apis/lanePlanner';
import AirCargoSection from './AirCargoSection';
import DoorToDoorSwitch from './DoorToDoorSwitch';
import OceanCargoSection from './OceanCargoSection';
import PlaceSearchBox from './PlaceSearchBox';
import RoadCargoSection from './RoadCargoSection';
import {
  buildLaneRequest,
  canTruck,
  defaultLaneFormValues,
  earliestShipDay,
  FLEXIBILITY_OPTIONS,
  LaneFormValues,
  needsTruckingCargo,
  laneFormValuesFromRequest,
  SearchMeta,
  searchMetaOf,
} from './laneForm';
import { PlaceValue, placeProblem } from './placeValue';
import { MODE_ORDER, MODE_TITLES } from './quoteView';
import styles from './styles.module.css';

interface LaneSearchFormProps {
  modes: LaneMode[];
  searching: boolean;
  onSearch: (request: LaneSearchRequest, meta: SearchMeta) => void;
  /** The company can add pickup and delivery trucking to air and ocean searches (`config.door_to_door`). */
  doorToDoor?: boolean;
  /** The Clear button emptied the form (the page also drops the results). */
  onClear?: () => void;
  /** A stored search to pre-populate the form with. The parent remounts the
   * form (key prop) when this changes, so initialValues apply. */
  prefill?: LaneSearchRequest | null;
}

const LaneSearchForm: React.FC<LaneSearchFormProps> = ({
  modes,
  searching,
  doorToDoor,
  onSearch,
  onClear,
  prefill,
}) => {
  const [form] = Form.useForm<LaneFormValues>();
  // Clear remounts the place boxes, so text typed but never picked goes too.
  const [clears, setClears] = useState(0);
  const origin = Form.useWatch('origin', form);
  const destination = Form.useWatch('destination', form);
  const wasResolving = useRef({ origin: false, destination: false });
  // A pick that settles (found or failed) re-checks its box, so the "still
  // finding" note from validating mid-lookup never outlives the lookup.
  useEffect(() => {
    (
      [
        ['origin', origin],
        ['destination', destination],
      ] as const
    ).forEach(([name, value]) => {
      if (wasResolving.current[name] && !value?.resolving) {
        const _ = form.validateFields([name]).catch((): void => undefined);
      }
      wasResolving.current[name] = Boolean(value?.resolving);
    });
  }, [origin, destination, form]);
  const ordered = MODE_ORDER.filter((mode) => modes.includes(mode));
  const multi = ordered.length > 1;
  const trucking = !!doorToDoor && canTruck(ordered);
  const doorOn = !!Form.useWatch('doorToDoor', form);
  // the cargo is only asked for while some end can be trucked (its fields are required)
  const truckingCargo =
    trucking &&
    needsTruckingCargo({ doorToDoor: doorOn, origin, destination }, ordered);
  const earliest = earliestShipDay(ordered);

  // Ticking a mode on can raise the earliest ship day (air and ocean differ).
  useEffect(() => {
    const current = form.getFieldValue('dropoffDate');
    if (current && current.isBefore(earliest, 'day')) {
      form.setFieldValue('dropoffDate', earliest);
    }
  }, [earliest, form]);

  const handleSubmit = async (): Promise<void> => {
    let validated: LaneFormValues;
    try {
      validated = await form.validateFields();
    } catch (err) {
      const first = (err as { errorFields?: { name: (string | number)[] }[] })
        .errorFields?.[0];
      if (first) {
        form.scrollToField(first.name, {
          behavior: 'smooth',
          block: 'center',
          focus: true,
        });
      }
      return;
    }
    const values = { ...form.getFieldsValue(true), ...validated };
    onSearch(
      buildLaneRequest(values, ordered, !!doorToDoor),
      searchMetaOf(values, ordered)
    );
  };

  const handleClear = (): void => {
    form.resetFields();
    form.setFieldsValue({
      ...defaultLaneFormValues(ordered),
      origin: undefined,
      destination: undefined,
    });
    setClears((n) => n + 1);
    onClear?.();
  };

  const sectionTitle = (mode: LaneMode, fallback: string): string =>
    multi ? `${fallback} · ${MODE_TITLES[mode]}` : fallback;

  return (
    <Form
      className={styles.quoteForm}
      form={form}
      layout='vertical'
      requiredMark
      colon={false}
      initialValues={
        prefill
          ? laneFormValuesFromRequest(prefill)
          : defaultLaneFormValues(modes)
      }
    >
      <div className={styles.sectionTitle}>Location</div>
      {(['origin', 'destination'] as const).map((end) => (
        <Form.Item
          key={`${end}-${clears}`}
          label={end === 'origin' ? 'Origin' : 'Destination'}
          name={end}
          validateTrigger='onBlur'
          rules={[
            {
              validator: async (_: unknown, value: PlaceValue | undefined) => {
                const problem = placeProblem(value, ordered);
                if (problem) throw new Error(problem);
              },
            },
          ]}
        >
          <PlaceSearchBox
            modes={ordered}
            placeholder='Search city, address, airport or port'
          />
        </Form.Item>
      ))}
      {trucking && <DoorToDoorSwitch modes={ordered} />}

      <div className={styles.sectionTitleSub}>Dates</div>
      <div className={`${styles.cargoRow} ${styles.cargoRowTwo}`}>
        <Form.Item
          label='Ship date'
          name='dropoffDate'
          rules={[{ required: true, message: 'Ship date is required.' }]}
        >
          <CustomDatePicker
            placeholder='Enter date'
            style={{ width: '100%' }}
            disabledDate={(current) =>
              !!current && current.isBefore(earliest, 'day')
            }
          />
        </Form.Item>
        <Form.Item
          label='Flexibility'
          name='flexibilityDays'
          tooltip='Shows prices for nearby days. A vendor that only searches the ship date says so above the results.'
        >
          <CustomSelect
            width='100%'
            height='36px'
            options={FLEXIBILITY_OPTIONS}
          />
        </Form.Item>
      </div>

      {ordered.includes('air') && (
        <AirCargoSection title={sectionTitle('air', 'Cargo')} />
      )}
      {ordered.includes('ocean') && (
        <OceanCargoSection title={sectionTitle('ocean', 'Cargo')} />
      )}
      {ordered.includes('road') && (
        <RoadCargoSection title={sectionTitle('road', 'Shipment')} />
      )}
      {truckingCargo && <RoadCargoSection title='Trucking cargo' ltlOnly />}

      <div className={styles.formFooter}>
        <button
          type='button'
          className={styles.primaryAction}
          onClick={() => handleSubmit()}
          disabled={searching}
        >
          {searching ? 'Searching…' : 'Search'}
        </button>
        <button
          type='button'
          className={styles.clearLink}
          onClick={handleClear}
        >
          Clear
        </button>
      </div>
    </Form>
  );
};

export default LaneSearchForm;
